import { Outlet } from 'react-router-dom'

import { Box, Spinner } from '@factorialco/deprecated-design-system'
import { PerformanceRootPageDocument } from 'generated/resources/graphql'

import AuthorizeUser from 'modules/core/components/AuthorizeUser'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import users from 'modules/core/store/collections/users'

import contractVersions from 'modules/contracts/store/collections/contractVersions'
import { hasRegularAccessOrNotEmployee } from 'modules/employees/lib/utils'
import { useManageableUserIds } from 'modules/performance/hooks/useManageableUserIds'
import CollectionsBootstrap from 'modules/performance/lib/CollectionsBootstrap'
import { PerformanceModuleContext } from 'modules/performance/lib/contexts'
import { PerformanceFeatureAvailability } from 'modules/performance/lib/featuresAvailability'
import { useReviewsFeatureSetup } from 'modules/reviews/hooks/useReviewsFeatureSetup'

const Container = () => {
  const { company, employee } = useCurrentSession()
  const manageableUserIDs: Set<number> = useManageableUserIds(users, contractVersions).get()

  const { loading: loadingContractVersions } = useAsyncData(
    () => contractVersions.fetch({ withCachedFetchParams: true }),
    []
  )

  const { loading: loadingReviewsSetup, error: errorReviewSetup } = useReviewsFeatureSetup()

  const { loading, data } = useQuery(PerformanceRootPageDocument, {
    variables: { companyId: company.id },
  })

  if (loadingReviewsSetup || loading || loadingContractVersions)
    return (
      <Box width="full" alignItems="center" height="full" justifyContent="center">
        <Spinner />
      </Box>
    )

  /**
   * Reviews Feature with paywall
   */
  const seeReviews = Boolean(
    data?.performanceReviews?._policies?.sidebarSeePerformanceReviewsSubLevelItem?.can
  )

  const seeReviewProcess = Boolean(
    data?.performance?._policies?.sidebarSeePerformanceReviewProcessSubLevelItem?.can
  )

  /**
   * Competencies Feature with paywall
   */
  const hasCompetencies = Boolean(
    data?.companies?.featureAvailabilitiesConnection?.nodes[0]?.available
  )
  const canSeeCompetenciesPermission = Boolean(
    data?.competencies?._policies?.assessmentsSeeEverybody?.can ||
      data?.competencies?._policies?.assessmentsSeeOwned?.can ||
      data?.competencies?._policies?.assessmentsSeeTeam?.can ||
      data?.competencies?._policies?.assessmentsSeeReportees?.can
  )
  // FIXME: Refactor this aberration. The amount of data fetched just to check
  // if there are manageable users is too much.
  const hasManageableUsers = !!Array.from(manageableUserIDs).length
  const seeCompetencies = hasCompetencies && canSeeCompetenciesPermission
  const performAssessments = hasCompetencies && hasManageableUsers

  /**
   * Goals Feature with paywall
   */
  const seeGoals = Boolean(data?.goals?._policies?.sidebarSeePerformanceGoalsSubLevelItem?.can)

  const performanceFeatureAvailability: PerformanceFeatureAvailability = {
    canSeeGoals: seeGoals,
    canSeeReviews: seeReviews,
    canSeeCompetencies: seeCompetencies,
    canPerformCompetencyAssessments: performAssessments,
    canSeeReviewProcess: seeReviewProcess,
    errorReviewSetup: !!errorReviewSetup,
  }

  const hasFullVisibility = Boolean(
    data?.performance?._policies?.reviewProcessFullVisibilityContextless?.can
  )

  return (
    <AuthorizeUser authorizeFn={() => hasRegularAccessOrNotEmployee(employee)}>
      <PerformanceModuleContext.Provider
        value={{
          performanceFeatureAvailability: performanceFeatureAvailability,
          hasFullVisibility,
        }}
      >
        <Outlet />
      </PerformanceModuleContext.Provider>
    </AuthorizeUser>
  )
}

const RootPage = () => {
  return (
    <CollectionsBootstrap>
      <Container />
    </CollectionsBootstrap>
  )
}

export default RootPage
